import { Col, Text } from 'components';
import { useTranslation } from 'react-i18next';
import { TableComponents } from 'screens/Admin/components';

const COUNTRY_LABEL_KEYS = {
  FRA: 'countries.france',
  DEU: 'countries.germany',
  JPN: 'countries.japan',
  MKD: 'countries.northMacedonia',
  POL: 'countries.poland',
  ZAF: 'countries.southAfrica',
  SWE: 'countries.sweden',
  GBR: 'countries.unitedKingdom',
  USA: 'countries.unitedStates'
};

const ChurnShell = ({ data, viewMode = 'country' }) => {
  const { t } = useTranslation();

  if (!data) {
    return (
      <Col wid="100%" marg="80px 0" centerAll noFlex>
        <Text className="text-[13px] text-[#97A3B5]">
          {t('subscriptions.admin.overview.churn.empty')}
        </Text>
      </Col>
    );
  }

  // All-time period — churn rate not calculated
  if (data.churnRate === null && data.cancelledCount === null) {
    return (
      <Col wid="100%" marg="80px 0" centerAll noFlex>
        <Text className="text-[28px] font-bold text-[#353E4A]">N/A</Text>
        <Text className="text-[13px] text-[#97A3B5] mt-2">
          {t('subscriptions.admin.overview.churn.allTimeNote')}
        </Text>
      </Col>
    );
  }

  const isCountryView = viewMode === 'country';
  const isPlanView = viewMode === 'plan';
  const isTeamView = viewMode === 'team';

  const rows = isCountryView
    ? data.byCountry || []
    : isPlanView
      ? data.byPlan || []
      : data.byTeam || [];

  const getCountryLabel = (countryCode) => {
    const key = COUNTRY_LABEL_KEYS[countryCode];
    if (!key) return countryCode || 'Unknown';
    return t(key);
  };

  return (
    <>
      {/* Summary bar */}
      <div className="flex items-center gap-8 px-6 py-4 border-b border-[#e5e7eb] bg-[#f8fafc]">
        <div>
          <p className="text-[11px] font-medium text-[#7989A0] uppercase tracking-wide mb-0.5">
            {t('subscriptions.admin.overview.churn.churnRate')}
          </p>
          <p className="text-[22px] font-bold text-[#353E4A]">
            {data.churnRate != null ? `${data.churnRate}%` : 'N/A'}
          </p>
        </div>
        <div>
          <p className="text-[11px] font-medium text-[#7989A0] uppercase tracking-wide mb-0.5">
            {t('subscriptions.admin.overview.churn.cancelled')}
          </p>
          <p className="text-[22px] font-bold text-[#353E4A]">{data.cancelledCount ?? 0}</p>
        </div>
        <div>
          <p className="text-[11px] font-medium text-[#7989A0] uppercase tracking-wide mb-0.5">
            {t('subscriptions.admin.overview.churn.activeDuringPeriod')}
          </p>
          <p className="text-[22px] font-bold text-[#353E4A]">{data.activeDuringPeriod ?? 0}</p>
        </div>
      </div>

      {/* Breakdown table */}
      {rows.length === 0 ? (
        <Col wid="100%" marg="60px 0" centerAll noFlex>
          <Text className="text-[13px] text-[#97A3B5]">
            {t('subscriptions.admin.overview.churn.empty')}
          </Text>
        </Col>
      ) : (
        <TableComponents.List
          loading={false}
          Header={() => (
            <>
              <TableComponents.Th first>
                <Text>
                  {isCountryView
                    ? t('subscriptions.admin.overview.churn.country')
                    : isPlanView
                      ? t('subscriptions.admin.overview.churn.plan')
                      : t('subscriptions.admin.overview.churn.teamName')}
                </Text>
              </TableComponents.Th>
              {isCountryView && (
                <TableComponents.Th>
                  <Text>{t('subscriptions.admin.overview.churn.countryCode')}</Text>
                </TableComponents.Th>
              )}
              {isPlanView && (
                <TableComponents.Th>
                  <Text>{t('subscriptions.admin.overview.churn.teamName')}</Text>
                </TableComponents.Th>
              )}
              <TableComponents.Th last>
                <Text>{t('subscriptions.admin.overview.churn.cancellations')}</Text>
              </TableComponents.Th>
            </>
          )}
          Items={() =>
            rows.map((row, i) => (
              <TableComponents.Tr
                key={
                  isCountryView
                    ? row.countryCode || i
                    : isPlanView
                      ? `${row.planId || row.title}-${row.teamId || i}`
                      : row.teamId || row.teamName || i
                }
              >
                <TableComponents.Td first>
                  <Text>
                    {isCountryView
                      ? getCountryLabel(row.countryCode)
                      : isPlanView
                        ? row.title || 'Unknown Plan'
                        : row.teamName || 'Unknown Team'}
                  </Text>
                </TableComponents.Td>
                {isCountryView && (
                  <TableComponents.Td>
                    <Text style={{ textTransform: 'uppercase' }}>{row.countryCode || '-'}</Text>
                  </TableComponents.Td>
                )}
                {isPlanView && (
                  <TableComponents.Td>
                    <Text>{row.teamName || '-'}</Text>
                  </TableComponents.Td>
                )}
                <TableComponents.Td last>
                  <Text>{row.cancelled}</Text>
                </TableComponents.Td>
              </TableComponents.Tr>
            ))
          }
        />
      )}
    </>
  );
};

export default ChurnShell;
